import React, { useEffect } from 'react';
import './LandingPage.css';
import { ConversationWidget } from './ConversationWidget';
import { ReactComponent as DataFlowSVG } from '../assets/data-flow.svg';
import { ReactComponent as ReportingSVG } from '../assets/reporting.svg';
import { ReactComponent as TeamChatSVG } from '../assets/team-chat.svg';
import { ReactComponent as DataFlowCircularSVG } from '../assets/data-flow-circular.svg';
import WaitlistSignup from './WaitlistSignup';

interface LandingPageProps {
  onNavigate: (section: string) => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onNavigate }) => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          } else {
            entry.target.classList.remove("visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll("section");
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { 
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    document.querySelectorAll('.fade-up, .scale-in').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const smoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <div className="landing-page">
      <div className="moving-lights">
        <div className="light"></div>
        <div className="light"></div>
        <div className="light"></div>
        <div className="light"></div>
        <div className="light"></div>
        <div className="light"></div>
      </div>
      <div className="parallax-wrapper">
        <div className="floating-circles">
          <div className="floating-circle circle-1"></div>
          <div className="floating-circle circle-2"></div>
          <div className="floating-circle circle-3"></div>
          <div className="floating-circle circle-4"></div>
          <div className="floating-circle circle-5"></div>
        </div>
        {/* Hero Section */}
        <section className="hero">
          <div className="hero-content">
            <h1>Your Personal Work Advocate</h1>
            <p>
              Your 1:1 time is valuable. Have a quick chat with Naya beforehand and let us transform your updates into 
              a structured, actionable report you can share with your manager - making every 1:1 
              more productive and meaningful.
            </p>
            <WaitlistSignup />
            <div className="cta-buttons">
              <a 
                href="#how-it-works" 
                onClick={(e) => smoothScroll(e, 'how-it-works')} 
                className="cta-button secondary"
              >
                How It Works
              </a>
              <a 
                href="#demo" 
                onClick={(e) => smoothScroll(e, 'demo')} 
                className="cta-button"
              >
                Try a 5-min Demo
              </a>
            </div>
          </div>
          <div className="hero-graphic">
            <div className="graphic-container">
              <div className="graphic-wrapper">
                <h3 className="graphic-label">Traditional Updates</h3>
                <DataFlowSVG className="left-graphic" />
                <p className="graphic-caption">Time-consuming meetings, unclear communication paths</p>
              </div>
              <div className="graphic-wrapper">
                <h3 className="graphic-label">With Naya</h3>
                <DataFlowCircularSVG className="right-graphic" />
                <p className="graphic-caption">Quick, natural conversations that ensure you're heard</p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="how-it-works">
          <h2>How Naya Works For You</h2>
          <div className="process-steps">
            <div className="process-step fade-up">
              <div className="step-header">
                <div className="step-number">1</div>
                <h3>5-Minute Chat</h3>
              </div>
              <p>Before your 1:1, spend 5 minutes sharing updates with Naya - 
                just like you're catching up with a colleague.</p>
              <div className="step-example">
                "Hey! What should we cover in your next 1:1? Any wins or challenges to discuss?"
              </div>
            </div>
            
            <div className="process-step fade-up">
              <div className="step-header">
                <div className="step-number">2</div>
                <h3>Instant Report</h3>
              </div>
              <p>Naya organizes your thoughts into a clear summary so you and your manager 
              can focus on what matters: meaningful discussion and decisions.</p>
              <div className="step-example">
                <ul>
                  <li>✓ Key Accomplishments</li>
                  <li>✓ Current Priorities</li>
                  <li>✓ Blockers & Support Needed</li>
                  <li>✓ Forward-Looking Plans</li>
                </ul>
              </div>
            </div>
            
            <div className="process-step fade-up">
              <div className="step-header">
                <div className="step-number">3</div>
                <h3>Better 1:1s</h3>
              </div>
              <p>Share the report before your 1:1. Your manager comes prepared, 
                making your discussions more meaningful and action-oriented.</p>
              <div className="step-example">
                <div className="report-preview">
                  <div className="report-item">📊 Pre-Read Summary</div>
                  <div className="report-item">🎯 Discussion Points</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Wavy Lines before Impact Section */}
        <div className="wavy-divider">
          <svg className="wavy-line front" viewBox="0 0 2400 100" preserveAspectRatio="none">
            <path d="M0,40 C200,80 400,0 600,40 C800,80 1000,0 1200,40 C1400,80 1600,0 1800,40 C2000,80 2200,0 2400,40" />
          </svg>
          <svg className="wavy-line back" viewBox="0 0 2400 100" preserveAspectRatio="none">
            <path d="M0,60 C200,20 400,100 600,60 C800,20 1000,100 1200,60 C1400,20 1600,100 1800,60 C2000,20 2200,100 2400,60" />
          </svg>
        </div>

        {/* Impact Section */}
        <section id="impact" className="impact">
          <h2>The Challenge of Being Heard</h2>
          <div className="impact-content">
            <div className="impact-row fade-up">
              <div className="impact-text">
                <h3>Limited Access to Leadership</h3>
                <p>Traditional communication channels often mean your important updates and needs get lost in the noise.</p>
              </div>
              <div className="impact-stat">
                <div className="impact-number">86%</div>
                <p>Of employees feel their ideas don't reach decision-makers</p>
              </div>
            </div>
            <div className="impact-row fade-up">
              <div className="impact-text">
                <h3>Time-Consuming Updates</h3>
                <p>Formal reports and structured updates take time away from your actual work.</p>
              </div>
              <div className="impact-stat">
                <div className="impact-number">4hrs</div>
                <p>Average time spent weekly on status updates and reports</p>
              </div>
            </div>
            <div className="impact-row fade-up">
              <div className="impact-text">
                <h3>Delayed Support</h3>
                <p>When challenges arise, getting the right attention and support can take too long.</p>
              </div>
              <div className="impact-stat">
                <div className="impact-number">73%</div>
                <p>Of work blockers take over a week to get addressed</p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section id="benefits" className="benefits">
          <div className="benefits-container">
            <div className="benefits-intro fade-up">
              <h2>Your Voice, Amplified</h2>
              <p>Get the recognition and support you deserve, without the extra work.</p>
            </div>
            <div className="benefits-grid-new">
              <div className="benefit-item scale-in">
                <div className="benefit-icon">🎯</div>
                <h3>Be Heard Clearly</h3>
                <p>Your updates and needs reach leadership in a format designed for action</p>
              </div>
              <div className="benefit-item scale-in">
                <div className="benefit-icon">⚡️</div>
                <h3>Save Time</h3>
                <p>Quick conversations replace lengthy reports and status meetings</p>
              </div>
              <div className="benefit-item scale-in">
                <div className="benefit-icon">🚀</div>
                <h3>Get Support Faster</h3>
                <p>Challenges and blockers get addressed quickly and effectively</p>
              </div>
              <div className="benefit-item scale-in">
                <div className="benefit-icon">💫</div>
                <h3>Showcase Your Impact</h3>
                <p>Ensure your contributions and achievements are visible to leadership</p>
              </div>
            </div>
          </div>
        </section>

        {/* Before Demo Section - Particle Stream */}
        <div className="particle-divider">
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
        </div>

        {/* Demo Section */}
        <section id="demo" className="demo">
          <h2>Try a 5-Minute Demo</h2>
          <p>Experience how Naya can transform a quick chat into a clear, focused update for your next 1:1.</p>
          <div className="demo-container">
            <div className="demo-instructions">
              <ul className="demo-features">
                <li><span className="step-num">1.</span> Click "Start Conversation" for a quick 5-minute chat.</li>
                <li><span className="step-num">2.</span> Share what you'd typically discuss in your 1:1.
                  <div className="example-updates">
                    <p>Try these examples:</p>
                    <ul>
                      <li>"I finished the customer integration ahead of schedule..."</li>
                      <li>"Need help prioritizing between two urgent requests..."</li>
                      <li>"Made good progress, but waiting on the security review..."</li>
                    </ul>
                  </div>
                </li>
                <li><span className="step-num">3.</span> Get an instant professional report ready to share.</li>
                <li><span className="step-num">4.</span> Preview how it would look in your manager's inbox.</li>
                <li><span className="step-num">5.</span> Tell us how we can make it more useful for your 1:1s!</li>
              </ul>
            </div>
            <ConversationWidget />
          </div>
          <p className="demo-disclaimer">
            <strong>Disclaimer:</strong> By participating in this demo, you acknowledge that we will have access to the conversation transcripts. Please share only information you are comfortable with and avoid disclosing sensitive or personal data.
          </p>
        </section>

        {/* Feedback Section */}
        <section id="feedback" className="feedback">
          <div className="feedback-content">
            <h2>Built With Your Input</h2>
            <p>Naya evolves with your feedback. Feel free to share your thoughts or 
              ask any questions about the future of team communication.</p>
            <a
              href="https://mail.google.com/mail/?view=cm&fs=1&to=team@meetnaya.com"
              className="cta-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact Us
            </a>
          </div>
        </section>

        {/* Built By Section */}
        <section id="built-by" className="built-by">
          <h2>Built by AI enthusiasts from</h2>
          <div className="companies-grid">
            <div className="company-logo">
              <img src="/companies/microsoft.svg" alt="Microsoft" />
            </div>
            <div className="company-logo">
              <img src="/companies/amazon.svg" alt="Amazon" />
            </div>
            <div className="company-logo">
              <img src="/companies/berkeley.svg" alt="UC Berkeley" />
            </div>
            <div className="company-logo">
              <img src="/companies/stanford.svg" alt="Stanford" />
            </div>
          </div>
        </section>

        {/* Footer Section */}
        <footer className="footer">
          <p>&copy; 2025 Naya AI. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;
